// Post.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import { finalizeEvent } from 'nostr-tools/pure';
import { hexToBytes } from '@noble/hashes/utils';
import CreatePost from './CreatePost';
import AuthPrompt from './AuthPrompt';
import { useSavedPosts } from './SavedPostsManager';
import { sanitizeMetadata, likeRateLimiter } from './utils/sanitize';

const Post = ({ post, relays, depth = 0 }) => {
  const { showProfile } = useProfile();
  const { translate } = useLanguage();
  const { publicKey, privateKey } = useAuth();
  const { isSaved, savePost, unsavePost } = useSavedPosts();
  
  const [username, setUsername] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [replies, setReplies] = useState([]);
  const [showReplies, setShowReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(0);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);


  const formatDate = (timestamp) => {
	const date = new Date(timestamp * 1000);
	const now = new Date();
	const diff = now - date;
	const seconds = Math.floor(diff / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (days > 7) {
	  return new Intl.DateTimeFormat('default', {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	  }).format(date);
	} else if (days > 0) {
	  return `${days}d ago`;
	} else if (hours > 0) {
	  return `${hours}h ago`;
	} else if (minutes > 0) {
	  return `${minutes}m ago`;
	} else {
	  return `${seconds}s ago`;
	}
  };

  // Format content with embedded media and links
  const formatContent = (content) => {
	const urlRegex = /(https?:\/\/[^\s]+)/g;
	const imageRegex = /\.(jpg|jpeg|png|gif|webp)(\?[^\s]*)?$/i;
	const videoRegex = /\.(mp4|webm)(\?[^\s]*)?$/i;
  const youtubeRegex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

	const parts = content.split(urlRegex);

	return parts.map((part, index) => {
	  if (urlRegex.test(part)) {
		// Handle YouTube videos
		const youtubeMatch = part.match(youtubeRegex);
		if (youtubeMatch) {
		  return (
			<div key={index} className="embedded-media youtube-container">
			  <iframe
				src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
				title="YouTube video"
			  />
			</div>
		  );
		}
		
		// Handle images
		if (imageRegex.test(part)) {
		  return (
			<div key={index} className="embedded-media image-container">
			  <img src={part} alt="Embedded content" loading="lazy" />
			</div>
		  );
		}

		// Handle videos
		if (videoRegex.test(part)) {
		  return (
			<div key={index} className="embedded-media video-container">
			  <video controls>
				<source src={part} />
				Your browser does not support the video tag.
			  </video>
			</div>
		  );
		}

		// Regular links
		return (
		  <a
			key={index}
			href={part}
			target="_blank"
			rel="noopener noreferrer"
			className="embedded-link"
		  >
			{part.length > 50 ? `${part.substring(0, 50)}...` : part}
		  </a>
		);
	  }
	  return <span key={index}>{part}</span>;
	});
  };
  
  const ShareButton = ({ post }) => {
	const [showCopyMessage, setShowCopyMessage] = useState(false);
	
	const handleShare = async () => {
	  const postUrl = `https://govstr.app/p/${post.id}`;
	  
	  // Check if Web Share API is available
	  if (navigator.share) {
		try {
		  await navigator.share({
			title: 'Govstr Post',
			text: post.content.slice(0, 100) + '...',
			url: postUrl
		  });
		} catch (err) {
		  if (err.name !== 'AbortError') {
			console.error('Error sharing:', err);
			// Fall back to copy
			await copyToClipboard(postUrl);
		  }
		}
	  } else {
		// Fall back to copy
		await copyToClipboard(postUrl);
	  }
	};
  
	const copyToClipboard = async (text) => {
	  try {
		await navigator.clipboard.writeText(text);
		setShowCopyMessage(true);
		setTimeout(() => setShowCopyMessage(false), 2000);
	  } catch (err) {
		console.error('Failed to copy:', err);
	  }
	};
  
	return (
	  <button 
		className="action-button"
		onClick={handleShare}
		aria-label="Share post"
	  >
		{showCopyMessage ? 'Link copied! 📋' : '🔗 Share'}
	  </button>
	);
  };

  // Fetch user metadata
	useEffect(() => {
	  const fetchMetadata = async () => {
		try {
		  const shortPubkey = `${post.pubkey.slice(0, 8)}...${post.pubkey.slice(-8)}`;
		  setUsername(shortPubkey);
  
		  // Check cache first
		  const cachedData = localStorage.getItem(`user-${post.pubkey}`);
		  if (cachedData) {
			const parsed = JSON.parse(cachedData);
			const cacheTime = parsed.timestamp;
			// Cache for 1 hour
			if (Date.now() - cacheTime < 3600000) {
			  setUsername(parsed.data.name || parsed.data.display_name || shortPubkey);
			  setProfileData(parsed.data);
			  return;
			}
		  }
  
		  // Fetch from relays if not cached
		  let fetchComplete = false;
		  for (const relay of relays) {
			if (fetchComplete) break;
  
			const ws = new WebSocket(relay);
			
			const fetchPromise = new Promise((resolve, reject) => {
			  const timeout = setTimeout(() => {
				ws.close();
				reject(new Error('Timeout'));
			  }, 5000);
  
			  ws.onopen = () => {
				ws.send(JSON.stringify([
				  "REQ",
				  "metadata",
				  {
					kinds: [0],
					authors: [post.pubkey],
					limit: 1
				  }
				]));
			  };
  
			  ws.onmessage = (event) => {
				try {
				  const [type, , data] = JSON.parse(event.data);
				  if (type === "EVENT" && data.kind === 0) {
					const rawMetadata = JSON.parse(data.content);
					// Sanitize metadata to prevent XSS
					const metadata = sanitizeMetadata(rawMetadata);
					setUsername(metadata.name || metadata.display_name || shortPubkey);
					setProfileData(metadata);
					// Cache with timestamp
					localStorage.setItem(`user-${post.pubkey}`, JSON.stringify({
					  data: metadata,
					  timestamp: Date.now()
					}));
					clearTimeout(timeout);
					fetchComplete = true;
					resolve();
					ws.close();
				  }
				} catch (e) {
				  console.error('Failed to parse metadata:', e);
				}
			  };
  
			  ws.onerror = () => {
				clearTimeout(timeout);
				reject(new Error('WebSocket error'));
			  };
			});
  
			try {
			  await fetchPromise;
			} catch (error) {
			  console.error(`Failed to fetch from ${relay}:`, error);
			}
		  }
		} catch (error) {
		  console.error('Failed to fetch metadata:', error);
		}
	  };
  
	  fetchMetadata();
	}, [post.pubkey, relays]);
  
  useEffect(() => {
	const fetchReplyCount = async () => {
	  try {
		let count = 0;
		let countComplete = false;
  
		for (const relay of relays) {
		  if (countComplete) break;
  
		  const ws = new WebSocket(relay);
		  
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 3000); // Shorter timeout for count
  
			ws.onopen = () => {
			  ws.send(JSON.stringify([
				"REQ",
				`count-${post.id}`,
				{
				  kinds: [1],
				  "#e": [post.id],
				}
			  ]));
			};
  
			ws.onmessage = (event) => {
			  try {
				const [type, , data] = JSON.parse(event.data);
				if (type === "EVENT") {
				  count++;
				}
				if (type === "EOSE") {
				  if (count > 0) {
					countComplete = true;
					setReplyCount(count);
				  }
				  clearTimeout(timeout);
				  ws.close();
				  resolve();
				}
			  } catch (e) {
				console.error('Failed to parse reply count:', e);
			  }
			};
  
			ws.onerror = () => {
			  clearTimeout(timeout);
			  resolve();
			};
		  });
		}
	  } catch (error) {
		console.error('Failed to fetch reply count:', error);
	  }
	};
  
	if (depth < 3) { // Only fetch reply count if we're not at max depth
	  fetchReplyCount();
	}
  }, [post.id, relays, depth]);
  
  useEffect(() => {
	const fetchLikes = async () => {
	  try {
		let count = 0;
		let userHasLiked = false;
  
		for (const relay of relays) {
		  const ws = new WebSocket(relay);
		  
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 3000);
  
			ws.onopen = () => {
			  ws.send(JSON.stringify([
				"REQ",
				`likes-${post.id}`,
				{
				  kinds: [7], // Reaction event
				  "#e": [post.id], // Reference to the post
				  limit: 100
				}
			  ]));
			};
  
			ws.onmessage = (event) => {
			  try {
				const [type, , data] = JSON.parse(event.data);
				if (type === "EVENT" && data.kind === 7 && data.content === "+") {
				  count++;
				  if (data.pubkey === publicKey) {
					userHasLiked = true;
				  }
				}
				if (type === "EOSE") {
				  clearTimeout(timeout);
				  ws.close();
				  resolve();
				}
			  } catch (e) {
				console.error('Failed to parse like:', e);
			  }
			};
  
			ws.onerror = () => {
			  clearTimeout(timeout);
			  resolve();
			};
		  });
		}
  
		setLikeCount(count);
		setHasLiked(userHasLiked);
	  } catch (error) {
		console.error('Failed to fetch likes:', error);
	  }
	};
  
	fetchLikes();
  }, [post.id, relays, publicKey]);

	const fetchReplies = async () => {
	  if (loadingReplies || depth >= 3) return;
	  setLoadingReplies(true);
  
	  try {
		let fetchedReplies = [];
		for (const relay of relays) {
		  const ws = new WebSocket(relay);
		  
		  await new Promise((resolve) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 5000);
  
			ws.onopen = () => {
			  ws.send(JSON.stringify([
				"REQ",
				"replies",
				{
				  kinds: [1],
				  "#e": [post.id],
				  limit: 50
				}
			  ]));
			};
  
			ws.onmessage = (event) => {
			  try {
				const [type, , data] = JSON.parse(event.data);
				if (type === "EVENT") {
				  if (!fetchedReplies.some(reply => reply.id === data.id)) {
					fetchedReplies.push(data);
				  }
				}
				if (type === "EOSE") {
				  clearTimeout(timeout);
				  resolve();
				  ws.close();
				}
			  } catch (e) {
				console.error('Failed to parse reply:', e);
			  }
			};
  
			ws.onerror = () => {
			  clearTimeout(timeout);
			  resolve();
			};
		  });
		}
  
		// Sort replies by timestamp
		fetchedReplies.sort((a, b) => a.created_at - b.created_at);
		setReplies(fetchedReplies);
		setReplyCount(fetchedReplies.length);
	  } catch (error) {
		console.error('Failed to fetch replies:', error);
	  } finally {
		setLoadingReplies(false);
	  }
	};

  const handleLike = async () => {
	if (!publicKey) {
	  setShowAuthPrompt(true);
	  return;
	}
  
	if (hasLiked) return; // Prevent double-liking

	// Rate limiting check
	if (!likeRateLimiter.isAllowed(publicKey)) {
	  console.warn('Like rate limit exceeded');
	  return;
	}
  
	try {
	  const event = {
		kind: 7,
		pubkey: publicKey,
		created_at: Math.floor(Date.now() / 1000),
		tags: [
		  ["e", post.id],
		  ["p", post.pubkey]
		],
		content: "+"
	  };
  
	  const signedEvent = finalizeEvent(event, hexToBytes(privateKey));
  
	  let published = false;
	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve();
			}, 3000);
  
			ws.onopen = () => {
			  ws.send(JSON.stringify(["EVENT", signedEvent]));
			};
  
			ws.onmessage = (msg) => {
			  try {
				const [type, , status] = JSON.parse(msg.data);
				if (type === "OK" && status === true) {
				  published = true;
				  clearTimeout(timeout);
				  ws.close();
				  resolve();
				}
			  } catch (e) {
				console.error('Failed to parse response:', e);
			  }
			};
  
			ws.onerror = () => {
			  clearTimeout(timeout);
			  resolve();
			};
		  });
		} catch (err) {
		  console.error(`Failed to publish to ${relay}:`, err);
		}
	  }
  
	  if (published) {
		setHasLiked(true);
		setLikeCount(prev => prev + 1);
	  }
	} catch (error) {
	  console.error('Failed to like post:', error);
	}
  };
  
 const handleProfileClick = (e) => {
   e.preventDefault();
   e.stopPropagation();
   if (profileData) {
	 showProfile(profileData, post.pubkey);
   }
 };

return (
    <article 
      className={`post ${depth > 0 ? 'reply' : ''}`}
      aria-label={`Post by ${username}`}
    >
      <header className="post-header">
        <button 
          className="user-link"
          onClick={handleProfileClick}
          aria-label={`View profile of ${username}`}
        >
          {profileData?.picture && (
            <img 
              src={profileData.picture} 
              alt={`${username}'s avatar`}
              className="profile-picture"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <span className="username">{username}</span>
        </button>
        <time 
          className="timestamp" 
          dateTime={new Date(post.created_at * 1000).toISOString()}
          title={new Date(post.created_at * 1000).toLocaleString()}
        >
          {formatDate(post.created_at)}
        </time>
      </header>

      <div className="post-content" role="article">
        {formatContent(post.content)}
      </div>

      <footer className="post-footer" role="group" aria-label="Post actions">
        <button 
          className={`action-button ${hasLiked ? 'liked' : ''}`}
          onClick={handleLike}
          disabled={hasLiked}
          aria-label={hasLiked ? `Liked. ${likeCount} likes` : `Like this post. ${likeCount} likes`}
          aria-pressed={hasLiked}
        >
          <span className="like-icon" aria-hidden="true">{hasLiked ? '❤️' : '🤍'}</span>
          <span className="like-count">{likeCount}</span>
        </button>

        <button 
          className="reply-button"
          onClick={() => {
            if (!publicKey && !showReplies) {
              setShowAuthPrompt(true);
              return;
            }
            if (!showReplies) {
              fetchReplies();
            }
            setShowReplies(!showReplies);
          }}
          aria-expanded={showReplies}
          aria-label={showReplies ? 'Hide replies' : `Show ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
        >
          <span aria-hidden="true">💬</span>
          {loadingReplies ? 
            translate('loading') : 
            showReplies ? 
              translate('hideReplies') : 
              `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
        </button>
        
        <ShareButton post={post} />
        
        <button 
          className={`action-button ${isSaved(post.id) ? 'saved' : ''}`}
          onClick={() => isSaved(post.id) ? unsavePost(post.id) : savePost(post.id)}
          aria-label={isSaved(post.id) ? 'Remove from saved posts' : 'Save this post'}
          aria-pressed={isSaved(post.id)}
        >
          <span className="save-icon" aria-hidden="true">{isSaved(post.id) ? '📥' : '💾'}</span>
          <span className="save-label">{isSaved(post.id) ? 'Saved' : 'Save'}</span>
        </button>
      </footer>

      {showReplies && (
        <section 
          className="replies-section" 
          aria-label={`Replies to ${username}'s post`}
        >
          {publicKey && (
            <CreatePost 
              replyTo={post.id} 
              onPostCreated={(newReply) => {
                setReplies(prev => [...prev, newReply]);
                setReplyCount(prev => prev + 1);
              }}
              relays={relays}
            />
          )}
          {replies.length > 0 && (
            <div role="feed" aria-label="Replies">
              {replies.map(reply => (
                <Post
                  key={reply.id}
                  post={reply}
                  relays={relays}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {showAuthPrompt && (
        <AuthPrompt onClose={() => setShowAuthPrompt(false)} />
      )}
    </article>
  );
};

export default Post;